import * as React from "react";
import ReactDOM from "react-dom";
import {
    Chart,
    ChartSeries,
    ChartSeriesItem,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartTitle,
    ChartLegend,
    exportVisual,
    ChartValueAxis,
    ChartValueAxisItem,
    ChartSeriesDefaults,
} from "@progress/kendo-react-charts";
import configData from "../../config.json";
import { GetSession, GetToken } from "../../components/Session";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { exportPDF } from "@progress/kendo-drawing";
import { saveAs } from "@progress/kendo-file-saver";
import { GetCloudLogin } from "../../WeighBridge/Helpers";

const cardStyle3D = {
    borderRadius: "10px",
    boxShadow:
        "0 6px 15px rgba(0, 0, 0, 0.25), inset 0 -3px 6px rgba(255, 255, 255, 0.3)",
    backgroundColor: "#fff",
};

const buttonStaticStyle = {
    borderRadius: "6px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
    cursor: "pointer",
    backgroundColor: "#fff",
    border: "1px solid #ccc",
    padding: "6px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

const modalStyles = {
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.6)",
        zIndex: 10000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
    },
    content: {
        backgroundColor: "white",
        borderRadius: "8px",
        width: "95vw",
        height: "95vh",
        padding: "20px",
        position: "relative",
        boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        perspective: "1000px",
    },
    closeBtnTopRight: {
        position: "absolute",
        top: "10px",
        right: "15px",
        fontSize: "28px",
        border: "none",
        background: "none",
        cursor: "pointer",
        color: "#333",
    },
    closeBtnBottom: {
        marginTop: "auto",
        alignSelf: "center",
        padding: "10px 25px",
        fontSize: "16px",
        borderRadius: "5px",
        border: "1px solid #333",
        background: "white",
        cursor: "pointer",
        color: "#333",
        transition: "background-color 0.2s ease",
    },
};

const InventoryTrend = () => {
    const [categories, setCategories] = React.useState([]);
    const [inOut, setInOut] = React.useState(null);
    const [productInventory, setProductInventory] = React.useState(null);
    const [accumulated, setAccumulated] = React.useState(null);
    const [pieData, setPieData] = React.useState(null);
    const [inoutMonths, setInoutMonths] = React.useState([]);
    const [topNProducts, setTopNProducts] = React.useState("Product Inventory");
    const [sites, setSites] = React.useState([]);
    const [site, setSite] = React.useState({ id: "0", name: "All" });
    const [products, setProducts] = React.useState([]);
    const [product, setProduct] = React.useState({ id: "0", name: "All" });
    const [modalChart, setModalChart] = React.useState(null);
    const [salesYAxis, setSalesYAxis] = React.useState("");
    const [productYAxis, setProductYAxis] = React.useState("");
    const [accYAxis, setAccYAxis] = React.useState("");

    const sale = React.useRef(null);
    const productChart = React.useRef(null);
    const total = React.useRef(null);
    const pieChart = React.useRef(null);
    const timer = React.useRef(null);
    const cloudToken = React.useRef("");

    React.useEffect(() => {
        GetCloudLogin((cred) => {
            cloudToken.current = cred === "" ? "" : cred.token;
        });

        const init = {
            method: "GET",
            accept: "application/json",
            headers: { Authorization: "Bearer " + GetToken() },
        };

        fetch(configData.SERVER_URL + "apiv7/options", init)
            .then((res) => res.json())
            .then((d) => {
                setSites([{ id: "0", name: "All" }, ...d.sites]);
                setSite({ id: "0", name: "All" });
                setProducts([{ id: "0", name: "All" }, ...d.products]);
                setProduct({ id: "0", name: "All" });
                refreshDashboard(0, 0);
            });
    }, []);

    const getSetting = (settings, name) => {
        if (!settings) return false;
        const st = settings.find((s) => s.name === name);
        if (!st) return undefined;
        let v = String(st.value);
        switch (st.type) {
            case 0:
                return v.toLowerCase() === "true";
            case 1:
                return parseInt(v);
            case 2:
                return parseFloat(v);
            default:
                return v;
        }
    };

    const refreshDashboard = (siteIdVal, productIdVal) => {
        const init = {
            method: "GET",
            accept: "application/json",
            headers: {
                Authorization:
                    "Bearer " + (cloudToken.current === "" || cloudToken.current === null ? GetToken() : cloudToken.current),
            },
        };
        const offset = new Date().getTimezoneOffset() / 60;
        const sess = GetSession();
        const baseUrl =
            cloudToken.current && cloudToken.current !== "" ? sess.CloudURL : configData.SERVER_URL;

        fetch(`${baseUrl}apiV7/dashboard/getinventorytrends?site=${siteIdVal}&product=${productIdVal}&offset=${offset}`, init)
            .then((res) => res.json())
            .then((d) => {
                setInOut(d.inOut);
                setProductInventory(d.productInventory);
                setAccumulated(d.accumulated);
                setPieData(d.pieData);
                setCategories(d.productNames);
                setTopNProducts(`Product Inventory (Top ${d.productNames.length})`);
                setInoutMonths(d.monthNames);
                setSalesYAxis(d.salesY);
                setProductYAxis(d.productY);
                setAccYAxis(d.accY);
            });
    };

    const onSiteChange = (e) => {
        setSite(e.target.value);
        refreshDashboard(e.target.value.id, product.id);
    };

    const onProductChange = (e) => {
        setProduct(e.target.value);
        refreshDashboard(site.id, e.target.value.id);
    };

    const onImageExportClick = (e) => {
        let chartVisual = null;
        switch (e.currentTarget.name) {
            case "sales":
                chartVisual = sale.current && exportVisual(sale.current);
                break;
            case "product":
                chartVisual = productChart.current && exportVisual(productChart.current);
                break;
            case "total":
                chartVisual = total.current && exportVisual(total.current);
                break;
            case "pie":
                chartVisual = pieChart.current && exportVisual(pieChart.current);
                break;
            default:
                break;
        }
        if (chartVisual) {
            exportPDF(chartVisual, { paperSize: "A4", landscape: true }).then((dataURI) => saveAs(dataURI, "Inventory.pdf"));
        }
    };

    const ExportZoomButton = ({ name, onClick, iconClass, ariaLabel }) => (
        <button
            type="button"
            name={name}
            onClick={onClick}
            aria-label={ariaLabel}
            style={buttonStaticStyle}
            className="k-button k-button-icon"
        >
            <i className={`fa ${iconClass}`} aria-hidden="true" />
        </button>
    );

    const ButtonsGroup = ({ exportName, onExportClick, onZoomClick }) => (
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginBottom: 8 }}>
            <ExportZoomButton name={exportName} onClick={onExportClick} iconClass="fa-download" ariaLabel="Export as PDF" />
            <ExportZoomButton onClick={onZoomClick} iconClass="fa-expand" ariaLabel="Expand Chart" />
        </div>
    );

    const renderModalChart = () => {
        if (!modalChart) return null;
        const commonProps = {
            style: {
                height: "85vh",
                width: "100%",
                filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
                borderRadius: "10px",
            },
            transitions: false,
        };
        switch (modalChart) {
            case "sales":
                return (
                    <Chart {...commonProps} ref={sale}>
                        <ChartTitle text="Inventory In/Out" font="22Pt bold" color="#00008B" />
                        <ChartLegend position="top" orientation="horizontal" />
                        <ChartCategoryAxis>
                            <ChartCategoryAxisItem categories={inoutMonths} startAngle={45} />
                        </ChartCategoryAxis>
                        <ChartValueAxis>
                            <ChartValueAxisItem title={{ text: salesYAxis }} />
                        </ChartValueAxis>
                        <ChartSeriesDefaults type="line" />
                        <ChartSeries>
                            {inOut.map((item, idx) => (
                                <ChartSeriesItem key={idx} type="line" tooltip={{ visible: true }} data={item.data} name={item.name} />
                            ))}
                        </ChartSeries>
                    </Chart>
                );
            case "product":
                return (
                    <Chart {...commonProps} ref={productChart}>
                        <ChartTitle text={topNProducts} font="22Pt bold" color="#00008B" />
                        <ChartLegend position="top" orientation="horizontal" />
                        <ChartCategoryAxis>
                            <ChartCategoryAxisItem categories={categories} startAngle={45} />
                        </ChartCategoryAxis>
                        <ChartValueAxis>
                            <ChartValueAxisItem title={{ text: productYAxis }} />
                        </ChartValueAxis>
                        <ChartSeriesDefaults type="column" />
                        <ChartSeries>
                            {productInventory.map((item, idx) => (
                                <ChartSeriesItem key={idx} type="column" tooltip={{ visible: true }} data={item.data} name={item.name} />
                            ))}
                        </ChartSeries>
                    </Chart>
                );
            case "total":
                return (
                    <Chart {...commonProps} ref={total}>
                        <ChartTitle text="Inventory Movement" font="22Pt bold" color="#00008B" />
                        <ChartLegend position="top" orientation="horizontal" />
                        <ChartCategoryAxis>
                            <ChartCategoryAxisItem categories={inoutMonths} startAngle={45} />
                        </ChartCategoryAxis>
                        <ChartValueAxis>
                            <ChartValueAxisItem title={{ text: accYAxis }} />
                        </ChartValueAxis>
                        <ChartSeriesDefaults type="area" />
                        <ChartSeries>
                            {accumulated.map((item, idx) => (
                                <ChartSeriesItem key={idx} type="area" tooltip={{ visible: true }} data={item.data} name={item.name} />
                            ))}
                        </ChartSeries>
                    </Chart>
                );
            case "pie":
                return (
                    <Chart {...commonProps} ref={pieChart}>
                        <ChartTitle text="Stockpile Inventory" font="22Pt bold" color="#00008B" />
                        <ChartLegend position="side" orientation="vertical" />
                        <ChartSeries>
                            <ChartSeriesItem type="column" tooltip={{ visible: true }} data={pieData} categoryField="name" field="share" />
                        </ChartSeries>
                    </Chart>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <div className="row" style={{ padding: "5px 10px" }}>
                <div className="col-md-3" style={{ minWidth: 180 }}>
                    <label className="control-label">Site</label>
                    <DropDownList
                        id="Site"
                        data={sites}
                        value={site}
                        textField="name"
                        dataItemKey="id"
                        onChange={onSiteChange}
                        style={{ width: "100%", minWidth: 150 }}
                        popupSettings={{ width: "auto" }}
                    />
                </div>
                <div className="col-md-3" style={{ minWidth: 180 }}>
                    <label className="control-label">Product</label>
                    <DropDownList
                        id="Product"
                        data={products}
                        value={product}
                        textField="name"
                        dataItemKey="id"
                        onChange={onProductChange}
                        style={{ width: "100%", minWidth: 150 }}
                        popupSettings={{ width: "auto" }}
                    />
                </div>
            </div>

            <div className="row mb-3">
                {inOut && (
                    <div className="col-6">
                        <ButtonsGroup
                            exportName="sales"
                            onExportClick={onImageExportClick}
                            onZoomClick={() => setModalChart("sales")}
                        />
                        <div className="k-card" style={cardStyle3D}>
                            <Chart
                                ref={sale}
                                style={{ height: 350, filter: "drop-shadow(0 0 5px rgba(0, 0, 0, 0.15))", borderRadius: "10px" }}
                                transitions={false}
                            >
                                <ChartTitle text="Inventory In/Out" font="22Pt bold" color="#00008B" />
                                <ChartLegend position="top" orientation="horizontal" />
                                <ChartCategoryAxis>
                                    <ChartCategoryAxisItem categories={inoutMonths} startAngle={45} />
                                </ChartCategoryAxis>
                                <ChartValueAxis>
                                    <ChartValueAxisItem title={{ text: salesYAxis }} />
                                </ChartValueAxis>
                                <ChartSeriesDefaults type="line" />
                                <ChartSeries>
                                    {inOut.map((item, idx) => (
                                        <ChartSeriesItem key={idx} type="line" tooltip={{ visible: true }} data={item.data} name={item.name} />
                                    ))}
                                </ChartSeries>
                            </Chart>
                        </div>
                    </div>
                )}

                {productInventory && (
                    <div className="col-6">
                        <ButtonsGroup
                            exportName="product"
                            onExportClick={onImageExportClick}
                            onZoomClick={() => setModalChart("product")}
                        />
                        <div className="k-card" style={cardStyle3D}>
                            <Chart
                                ref={productChart}
                                style={{ height: 350, filter: "drop-shadow(0 0 5px rgba(0, 0, 0, 0.15))", borderRadius: "10px" }}
                                transitions={false}
                            >
                                <ChartTitle text={topNProducts} font="22Pt bold" color="#00008B" />
                                <ChartLegend position="top" orientation="horizontal" />
                                <ChartCategoryAxis>
                                    <ChartCategoryAxisItem categories={categories} startAngle={45} />
                                </ChartCategoryAxis>
                                <ChartValueAxis>
                                    <ChartValueAxisItem title={{ text: productYAxis }} />
                                </ChartValueAxis>
                                <ChartSeriesDefaults type="column" />
                                <ChartSeries>
                                    {productInventory.map((item, idx) => (
                                        <ChartSeriesItem key={idx} type="column" tooltip={{ visible: true }} data={item.data} name={item.name} />
                                    ))}
                                </ChartSeries>
                            </Chart>
                        </div>
                    </div>
                )}
            </div>

            <div className="row">
                {accumulated && (
                    <div className="col-6">
                        <ButtonsGroup
                            exportName="total"
                            onExportClick={onImageExportClick}
                            onZoomClick={() => setModalChart("total")}
                        />
                        <div className="k-card" style={cardStyle3D}>
                            <Chart
                                ref={total}
                                style={{ height: 350, filter: "drop-shadow(0 0 5px rgba(0, 0, 0, 0.15))", borderRadius: "10px" }}
                                transitions={false}
                            >
                                <ChartTitle text="Inventory Movement" font="22Pt bold" color="#00008B" />
                                <ChartLegend position="top" orientation="horizontal" />
                                <ChartCategoryAxis>
                                    <ChartCategoryAxisItem categories={inoutMonths} startAngle={45} />
                                </ChartCategoryAxis>
                                <ChartValueAxis>
                                    <ChartValueAxisItem title={{ text: accYAxis }} />
                                </ChartValueAxis>
                                <ChartSeriesDefaults type="area" />
                                <ChartSeries>
                                    {accumulated.map((item, idx) => (
                                        <ChartSeriesItem key={idx} type="area" tooltip={{ visible: true }} data={item.data} name={item.name} />
                                    ))}
                                </ChartSeries>
                            </Chart>
                        </div>
                    </div>
                )}

                {pieData && (
                    <div className="col-6">
                        <ButtonsGroup
                            exportName="pie"
                            onExportClick={onImageExportClick}
                            onZoomClick={() => setModalChart("pie")}
                        />
                        <div className="k-card" style={cardStyle3D}>
                            <Chart
                                ref={pieChart}
                                style={{ height: 350, filter: "drop-shadow(0 0 5px rgba(0, 0, 0, 0.15))", borderRadius: "10px" }}
                                transitions={false}
                            >
                                <ChartTitle text="Stockpile Inventory" font="22Pt bold" color="#00008B" />
                                <ChartLegend position="side" orientation="vertical" />
                                <ChartSeries>
                                    <ChartSeriesItem type="column" tooltip={{ visible: true }} data={pieData} categoryField="name" field="share" />
                                </ChartSeries>
                            </Chart>
                        </div>
                    </div>
                )}
            </div>

            {modalChart &&
                ReactDOM.createPortal(
                    <div style={modalStyles.overlay} onClick={() => setModalChart(null)}>
                        <div style={modalStyles.content} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                            <button style={modalStyles.closeBtnTopRight} aria-label="Close" onClick={() => setModalChart(null)}>
                                &times;
                            </button>
                            {renderModalChart()}
                            <button style={modalStyles.closeBtnBottom} onClick={() => setModalChart(null)}>
                                Close
                            </button>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default InventoryTrend;
